// src/utils/clinicQueue.js
//
// Pure logic for the front-desk "Today's clinic" board (pages/ClinicQueuePage.jsx).
// No React, no network: everything takes QueueEntry[] (see
// docs/visit-workflow-contract.md) plus an explicit `now`, so it is testable.

// A patient waiting longer than this is flagged on the board and in the summary.
export const LONG_WAIT_MINUTES = 20;

// Board columns in display order. `statuses` are the appointment statuses that
// land in the column. CANCELLED and NO_SHOW sit in Done (greyed, with their own
// badge) so the day's whole list stays accountable on one screen.
export const COLUMNS = [
  { key: 'expected',       label: 'Expected',        statuses: ['SCHEDULED', 'CONFIRMED'] },
  { key: 'waiting',        label: 'Waiting',         statuses: ['ARRIVED'] },
  { key: 'inConsultation', label: 'In consultation', statuses: ['IN_CONSULTATION'] },
  { key: 'done',           label: 'Done',            statuses: ['COMPLETED', 'NO_SHOW', 'CANCELLED'] },
];

const statusOf = (e) => String(e?.status || '').toUpperCase();

// Accepts ISO local date-time ("2026-10-30T09:05:00"), ISO with zone, or a bare
// "HH:mm[:ss]" time (scheduledTime may be either); returns epoch ms or null.
export function parseTimestamp(value, now = new Date()) {
  if (!value) return null;
  const s = String(value);
  const t = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/.exec(s);
  if (t) {
    const d = new Date(now);
    d.setHours(Number(t[1]), Number(t[2]), Number(t[3] || 0), 0);
    return d.getTime();
  }
  const ms = new Date(s).getTime();
  return Number.isNaN(ms) ? null : ms;
}

const toMs = (now) => (now instanceof Date ? now.getTime() : (typeof now === 'number' ? now : Date.now()));

// Minutes the patient has waited (waiting) or waited before being seen
// (in consultation / done). Waiting entries tick against `now` so the board
// stays correct between refreshes; otherwise the server's waitingMinutes is
// the fallback. Returns null when nothing is known (e.g. an Expected card).
export function waitMinutes(entry, now = new Date()) {
  const nowMs = toMs(now);
  const status = statusOf(entry);
  const arrived = parseTimestamp(entry?.arrivedAt, nowMs);
  if (status === 'ARRIVED' && arrived != null) {
    return Math.max(0, Math.floor((nowMs - arrived) / 60000));
  }
  if (status !== 'ARRIVED') {
    const started = parseTimestamp(entry?.consultationStartedAt, nowMs);
    if (arrived != null && started != null) return Math.max(0, Math.floor((started - arrived) / 60000));
  }
  return typeof entry?.waitingMinutes === 'number' ? Math.max(0, entry.waitingMinutes) : null;
}

// Nulls sort last whichever direction.
function compareBy(getter, dir = 1) {
  return (a, b) => {
    const x = getter(a); const y = getter(b);
    if (x == null && y == null) return 0;
    if (x == null) return 1;
    if (y == null) return -1;
    return (x - y) * dir;
  };
}

const arrivedMs   = (e) => parseTimestamp(e.arrivedAt);
const scheduledMs = (e) => parseTimestamp(e.scheduledTime);
const startedMs   = (e) => parseTimestamp(e.consultationStartedAt);

// Longest-waiting first; ties (and missing arrivedAt) fall back to scheduled time.
const byArrival = (a, b) => compareBy(arrivedMs)(a, b) || compareBy(scheduledMs)(a, b);

const SORTERS = {
  expected: compareBy(scheduledMs),
  waiting: byArrival,
  inConsultation: (a, b) => compareBy(startedMs)(a, b) || byArrival(a, b),
  // Most recently finished at the top.
  done: (a, b) => compareBy((e) => startedMs(e) ?? arrivedMs(e) ?? scheduledMs(e), -1)(a, b),
};

// Splits entries into the four board columns (sorted) plus `other` for any
// status this client does not know, so a future backend status never vanishes
// silently — the page surfaces it instead.
export function bucketQueue(entries) {
  const out = { expected: [], waiting: [], inConsultation: [], done: [], other: [] };
  (Array.isArray(entries) ? entries : []).forEach((e) => {
    const col = COLUMNS.find((c) => c.statuses.includes(statusOf(e)));
    out[col ? col.key : 'other'].push(e);
  });
  COLUMNS.forEach((c) => out[c.key].sort(SORTERS[c.key]));
  return out;
}

// Summary badges. avgWaitMinutes averages everyone who has arrived (still
// waiting, in consultation or done) and is null when nobody has.
export function summaryStats(entries, now = new Date()) {
  const list = Array.isArray(entries) ? entries : [];
  const waits = [];
  let waitingCount = 0;
  let waitingOver = 0;
  let noShows = 0;
  list.forEach((e) => {
    const status = statusOf(e);
    if (status === 'NO_SHOW') noShows += 1;
    if (!['ARRIVED', 'IN_CONSULTATION', 'COMPLETED'].includes(status)) return;
    const w = waitMinutes(e, now);
    if (w != null) waits.push(w);
    if (status === 'ARRIVED') {
      waitingCount += 1;
      if (w != null && w > LONG_WAIT_MINUTES) waitingOver += 1;
    }
  });
  return {
    total: list.length,
    waitingCount,
    avgWaitMinutes: waits.length ? Math.round(waits.reduce((s, w) => s + w, 0) / waits.length) : null,
    waitingOver20: waitingOver,
    noShows,
  };
}

export function isLongWait(entry, now = new Date()) {
  const w = waitMinutes(entry, now);
  return statusOf(entry) === 'ARRIVED' && w != null && w > LONG_WAIT_MINUTES;
}

// 0 -> "<1 min", 45 -> "45 min", 75 -> "1 h 15 min", null -> "—".
export function formatWait(minutes) {
  if (minutes == null) return '—';
  if (minutes < 1) return '<1 min';
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60); const m = minutes % 60;
  return m ? `${h} h ${m} min` : `${h} h`;
}

// "14:30:00" or "2026-10-30T14:30:00" -> "2:30 PM"; unknown -> "".
export function formatClock(value) {
  const ms = parseTimestamp(value);
  if (ms == null) return '';
  const d = new Date(ms);
  const h = d.getHours();
  return `${h % 12 || 12}:${String(d.getMinutes()).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`;
}

export const isWalkIn = (entry) => String(entry?.appointmentType || '').toUpperCase() === 'WALK_IN';

// Local YYYY-MM-DD (not toISOString, which is UTC and flips the day in India mornings).
export function todayIso(now = new Date()) {
  const p = (n) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())}`;
}
